// src/components/TeamSheet.js
import React from 'react';
import '../styles/TeamSheet.css';

/**
 * TeamSheet component - displays a rugby team lineup in formation
 * Layout matches standard rugby union 15-a-side formation
 */
const TeamSheet = ({ players, teamName }) => {
    if (!players || players.length === 0) {
        return (
            <div className="team-sheet">
                <div className="team-sheet-header">
                    <h3>{teamName || 'Team'}</h3>
                </div>
                <div className="no-players">No team sheet available</div>
            </div>
        );
    }

    // Group players by position number
    const playersByPosition = {};
    players.forEach(player => {
        const posNum = player.position_number || player.positionNumber || 0;
        if (!playersByPosition[posNum]) {
            playersByPosition[posNum] = [];
        }
        playersByPosition[posNum].push(player);
    });

    // Get player for a position (first player if multiple)
    const getPlayer = (posNum) => {
        const playersAtPos = playersByPosition[posNum];
        return playersAtPos && playersAtPos.length > 0 ? playersAtPos[0] : null;
    };

    // Render a player circle
    const renderPlayer = (posNum, label) => {
        const player = getPlayer(posNum);
        return (
            <div className="player-position" title={player ? `${posNum}. ${player.name}` : label}>
                <div className="player-number">{posNum}</div>
                {player && <div className="player-name">{player.name}</div>}
            </div>
        );
    };

    // Get substitutes (position 16+)
    const substitutes = players.filter(p => {
        const posNum = p.position_number || p.positionNumber || 0;
        return posNum >= 16;
    });

    return (
        <div className="team-sheet">
            <div className="team-sheet-header">
                <h3>{teamName || 'Team'}</h3>
            </div>

            <div className="rugby-field">
                {/* Full Back - 15 */}
                <div className="field-row fullback-row">
                    {renderPlayer(15, 'Full Back')}
                    <div className="position-label">Full Back</div>
                </div>

                {/* Three Quarters - 11, 14 (Wings) */}
                <div className="field-row wings-row">
                    <div className="wing-left">
                        {renderPlayer(11, 'Left Wing')}
                        <div className="position-label">Left Wing</div>
                    </div>
                    <div className="wing-right">
                        {renderPlayer(14, 'Right Wing')}
                        <div className="position-label">Right Wing</div>
                    </div>
                </div>

                {/* Three Quarters - 12, 13 (Centres) */}
                <div className="field-row centres-row">
                    {renderPlayer(12, 'Inside Centre')}
                    <div className="position-label">Inside Centre</div>
                    {renderPlayer(13, 'Outside Centre')}
                    <div className="position-label">Outside Centre</div>
                </div>

                {/* Half Backs - 10, 9 */}
                <div className="field-row halfbacks-row">
                    {renderPlayer(10, 'Out Half')}
                    <div className="position-label">Out Half</div>
                </div>

                <div className="field-row scrumhalf-row">
                    {renderPlayer(9, 'Scrum Half')}
                    <div className="position-label">Scrum Half</div>
                </div>

                {/* Back Row - 6, 7, 8 */}
                <div className="field-row backrow-row">
                    <div className="flanker-left">
                        {renderPlayer(6, 'Blindside Flanker')}
                        <div className="position-label">Blindside Flanker</div>
                    </div>
                    <div className="number-eight">
                        {renderPlayer(8, 'Number 8')}
                        <div className="position-label">Number 8</div>
                    </div>
                    <div className="flanker-right">
                        {renderPlayer(7, 'Openside Flanker')}
                        <div className="position-label">Openside Flanker</div>
                    </div>
                </div>

                {/* Second Row - 4, 5 */}
                <div className="field-row secondrow-row">
                    {renderPlayer(4, 'Second Row')}
                    <div className="position-label">Second Row</div>
                    {renderPlayer(5, 'Second Row')}
                    <div className="position-label">Second Row</div>
                </div>

                {/* Front Row - 1, 2, 3 */}
                <div className="field-row frontrow-row">
                    <div className="prop-left">
                        {renderPlayer(1, 'Loosehead Prop')}
                        <div className="position-label">Loosehead Prop</div>
                    </div>
                    <div className="hooker">
                        {renderPlayer(2, 'Hooker')}
                        <div className="position-label">Hooker</div>
                    </div>
                    <div className="prop-right">
                        {renderPlayer(3, 'Tighthead Prop')}
                        <div className="position-label">Tighthead Prop</div>
                    </div>
                </div>
            </div>

            {/* Substitutes */}
            {substitutes.length > 0 && (
                <div className="substitutes-section">
                    <h4>Substitutes</h4>
                    <div className="substitutes-list">
                        {substitutes.map((player, idx) => (
                            <div key={idx} className="substitute-player">
                                <span className="sub-number">
                                    {player.position_number || player.positionNumber || 16}
                                </span>
                                <span className="sub-name">{player.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamSheet;
